import { decode } from '@here/flexpolyline';
import { addSeconds } from 'date-fns';
import { format } from 'date-fns-tz';
import { sum } from 'lodash';
import sortBy from 'lodash/fp/sortBy';

import type { Coordinates, Waypoint } from '../../../application/redux/search/searchReducer';
import type { AdditionalParams } from '../fetchRoutes';
import type { WaypointWithCoordinates } from '../fetchWaypointCoordinates';

interface Summary {
    distance: number;
    travelTime: number;
    departure: string;
    arrival: string;
}

interface Costs {
    totalCost: number;
    vehicleCost: number;
    tollCost: number;
    currency: string;
}

export interface Route {
    waypoints: WaypointWithCoordinates[];
    routeCoordinates: Coordinates[];
    summary: Summary;
    costs: Costs;
}

interface HereLocation {
    lat: number;
    lng: number;
}

interface HerePlace {
    location: HereLocation;
}

interface Tolls {
    total: {
        value: number;
    };
}

interface HereSection {
    departure: {
        place: HerePlace;
    };
    arrival: {
        place: HerePlace;
    };
    summary?: {
        duration: number;
        length: number;
        tolls?: Tolls;
    };
    polyline?: string;
}

interface HereRoute {
    sections: HereSection[];
}

const extractCoordinatesFromRoute = (route: HereRoute): Coordinates[] =>
    route.sections
        .filter(s => s.polyline && s.polyline.length > 0)
        .flatMap(section =>
            decode(section.polyline ?? '').polyline.map((latLong: number[]) => ({
                lat: Number(latLong[0].toFixed(5)),
                lng: Number(latLong[1].toFixed(5)),
            }))
        );

const buildWaypointWithCoordinates = (waypoint: Waypoint, location: HereLocation): WaypointWithCoordinates => ({
    id: waypoint.id,
    coordinates: { lat: location.lat, lng: location.lng },
    address: waypoint.address,
});

const extractAndFormatWaypoints = (route: HereRoute, waypointsFromSearch: Waypoint[]): WaypointWithCoordinates[] =>
    waypointsFromSearch.reduce(
        (previousValue: WaypointWithCoordinates[], currentValue: Waypoint, currentIndex: number) => {
            const location =
                currentIndex === 0
                    ? route.sections[0].departure.place.location
                    : route.sections[currentIndex - 1].arrival.place.location;
            const waypoint = buildWaypointWithCoordinates(currentValue, location);
            return [...previousValue, waypoint];
        },
        []
    );

const extractSummaryFromRoute = (
    route: HereRoute,
    departureDateTime: Date,
    additionalParams: AdditionalParams
): Summary => {
    const arrivalDepartureEmpty = {
        arrival: '',
        departure: '',
    };
    const summary = route.sections.reduce(
        (prevSummary: Summary, currentSection: HereSection) => ({
            distance: prevSummary.distance + (currentSection.summary?.length ?? 0),
            travelTime: prevSummary.travelTime + (currentSection.summary?.duration ?? 0),
            ...arrivalDepartureEmpty,
        }),
        { distance: 0, travelTime: 0, ...arrivalDepartureEmpty } satisfies Summary
    );

    const options = { timeZone: additionalParams.timezone };
    return {
        ...summary,
        departure: format(departureDateTime, "yyyy-MM-dd'T'HH:mm:ssx", options),
        arrival: format(addSeconds(departureDateTime, summary.travelTime), "yyyy-MM-dd'T'HH:mm:ssx", options),
    };
};

const extractCostsFromRoute = (route: HereRoute, summary: Summary, additionalParams: AdditionalParams): Costs => {
    const tollCost = sum(route.sections.map(section => section.summary?.tolls?.total?.value ?? 0));
    const vehicleCost = (summary.distance / 1000) * additionalParams.vehicleCostsPerKilometer;

    return {
        totalCost: vehicleCost + tollCost,
        vehicleCost,
        tollCost,
        currency: additionalParams.currency,
    };
};

const sortByTravelTime = sortBy<Route>('summary.travelTime');

const mapRoute = (route: HereRoute, waypointsFromSearch: Waypoint[], additionalParams: AdditionalParams): Route => {
    const summary = extractSummaryFromRoute(route, additionalParams.departureDateTime, additionalParams);
    const costs = extractCostsFromRoute(route, summary, additionalParams);
    return {
        waypoints: extractAndFormatWaypoints(route, waypointsFromSearch),
        routeCoordinates: extractCoordinatesFromRoute(route),
        summary,
        costs,
    };
};

export const mapRoutes = (
    routingResult: { routes: HereRoute[] },
    waypointsFromSearch: Waypoint[],
    additionalParams: AdditionalParams
): Route[] =>
    sortByTravelTime(
        routingResult.routes.map((route: HereRoute) => mapRoute(route, waypointsFromSearch, additionalParams))
    );
